import React, { useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dropdown } from 'semantic-ui-react';
import styles from './styles.module.scss';

const messages = defineMessages({
  editChannels: {
    id: 'editChannels',
    defaultMessage: 'Edit channels (Drag and Drop)',
  },
  addChannel: { id: 'addChannel', defaultMessage: 'Add channel' },
  dragHandle: { id: 'dragHandle', defaultMessage: 'Drag' },
  namePlaceholder: { id: 'namePlaceholder', defaultMessage: 'Channel name' },
  linkPlaceholder: { id: 'linkPlaceholder', defaultMessage: 'Add link' },
  removeChannel: { id: 'removeChannel', defaultMessage: 'Remove' },
});

const DraggableChannel = ({
  channel,
  index,
  updateChannel,
  updateChannelColor,
  updateChannelIcon,
  updateChannelLink,
  removeChannel,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: index });
  const intl = useIntl();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: channel.color,
    padding: '10px',
    margin: '10px',
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  };

  const iconOptions = [
    { key: 'hat', value: 'hat-icon', text: 'Hat' },
    { key: 'jar', value: 'jar-icon', text: 'Jar' },
    { key: 'music', value: 'music-icon', text: 'Music' },
    { key: 'podcast', value: 'podcast-icon', text: 'Podcast' },
    { key: 'running', value: 'running-icon', text: 'Running' },
  ];

  const stopDragPropagation = (e) => {
    e.stopPropagation();
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <button {...listeners} style={{ cursor: 'grab', marginBottom: '10px' }}>
        {intl.formatMessage(messages.dragHandle)}
      </button>
      <Dropdown
        placeholder="Valitse ikoni"
        selection
        options={iconOptions}
        value={channel.icon}
        onChange={(e, { value }) => updateChannelIcon(index, value)}
        onMouseDown={stopDragPropagation}
        style={{
          marginBottom: '10px',
          width: '100%',
          clipPath: 'none' /* Estää leikkaamisen */, // Korkea z-index, jotta se ei jää parentin alle
        }}
        menu={{ attached: false }} // Pakottaa dropdownin ulos parentista
        usePortal={true} // Varmistaa, että dropdown siirtyy bodyyn
      />
      {channel.icon && (
        <div
          className={`${styles.customIcon} ${channel.icon}`}
          style={{
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            padding: '10px',
            maxWidth: '40px',
            width: '100%',
            height: '100px',
            marginBottom: '10px',
          }}
        ></div>
      )}
      <input
        type="text"
        value={channel.name}
        onChange={(e) => updateChannel(index, e.target.value)}
        onMouseDown={stopDragPropagation}
        placeholder={intl.formatMessage(messages.namePlaceholder)}
        style={{ marginBottom: '10px', width: '100%' }}
      />
      <input
        type="color"
        value={channel.color}
        onChange={(e) => updateChannelColor(index, e.target.value)}
        onMouseDown={stopDragPropagation}
        style={{ marginBottom: '10px', width: '100%' }}
      />
      <input
        type="text"
        value={channel.link || ''}
        onChange={(e) => updateChannelLink(index, e.target.value)}
        onMouseDown={stopDragPropagation}
        placeholder={intl.formatMessage(messages.linkPlaceholder)}
        style={{ marginBottom: '10px', width: '100%' }}
      />
      <button
        onClick={(e) => {
          e.stopPropagation();
          removeChannel(index);
        }}
        style={{ marginTop: '10px' }}
      >
        {intl.formatMessage(messages.removeChannel)}
      </button>
    </div>
  );
};

const Edit = ({ data, block, onChangeBlock }) => {
  const intl = useIntl();
  const [channels, setChannels] = useState(data.channels || []);

  const updateChannel = (index, name) => {
    const updatedChannels = [...channels];
    updatedChannels[index].name = name;
    setChannels(updatedChannels);
    onChangeBlock(block, { ...data, channels: updatedChannels });
  };

  const updateChannelColor = (index, color) => {
    const updatedChannels = [...channels];
    updatedChannels[index].color = color;
    setChannels(updatedChannels);
    onChangeBlock(block, { ...data, channels: updatedChannels });
  };

  const updateChannelIcon = (index, icon) => {
    const updatedChannels = [...channels];
    updatedChannels[index].icon = icon;
    setChannels(updatedChannels);
    onChangeBlock(block, { ...data, channels: updatedChannels });
  };

  const updateChannelLink = (index, link) => {
    const updatedChannels = [...channels];
    updatedChannels[index].link = link;
    setChannels(updatedChannels);
    onChangeBlock(block, { ...data, channels: updatedChannels });
  };

  const removeChannel = (index) => {
    const updatedChannels = channels.filter((_, i) => i !== index);
    setChannels(updatedChannels);
    onChangeBlock(block, { ...data, channels: updatedChannels });
  };

  const addChannel = () => {
    const newChannels = [
      ...channels,
      { name: '', color: '#CCCCCC', icon: '', link: '' },
    ];
    setChannels(newChannels);
    onChangeBlock(block, { ...data, channels: newChannels });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setChannels((channels) => {
        const oldIndex = parseInt(active.id, 10);
        const newIndex = parseInt(over.id, 10);
        const updatedChannels = arrayMove(channels, oldIndex, newIndex);
        onChangeBlock(block, { ...data, channels: updatedChannels });
        return updatedChannels;
      });
    }
  };

  return (
    <div>
      <h4>{intl.formatMessage(messages.editChannels)}</h4>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={channels.map((_, index) => index)}
          strategy={horizontalListSortingStrategy}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              overflowX: 'auto',
              overflowY: 'visible',
              position: 'relative',
              paddingBottom: '10px',
            }}
          >
            {channels.map((channel, index) => (
              <DraggableChannel
                key={index}
                channel={channel}
                index={index}
                updateChannel={updateChannel}
                updateChannelColor={updateChannelColor}
                updateChannelIcon={updateChannelIcon}
                updateChannelLink={updateChannelLink}
                removeChannel={removeChannel}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <button onClick={addChannel} style={{ marginTop: '10px' }}>
        {intl.formatMessage(messages.addChannel)}
      </button>
    </div>
  );
};

export default Edit;
